import React from 'react';
import {
  Menu as MuiMenu,
  MenuProps as MuiMenuProps,
  MenuItem,
  MenuList,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  IconButtonProps,
  Paper,
  ClickAwayListener,
  Popper,
  Grow,
  Fade,
} from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledMenu = styled(MuiMenu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: `calc(${theme.shape.borderRadius}px * 1.5)`,
    marginTop: theme.spacing(1),
    minWidth: 180,
    boxShadow: theme.shadows[8],
    '& .MuiMenuItem-root': {
      padding: theme.spacing(1, 2),
      borderRadius: theme.shape.borderRadius,
      margin: theme.spacing(0.25, 1),
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
      '&.Mui-disabled': {
        opacity: 0.5,
      },
      '& .MuiListItemIcon-root': {
        minWidth: 36,
        color: theme.palette.text.secondary,
      },
      '& .MuiListItemText-primary': {
        fontSize: theme.typography.body2.fontSize,
      },
      '& .MuiListItemText-secondary': {
        fontSize: theme.typography.caption.fontSize,
      },
    },
  },
}));

const ContextMenuContainer = styled(Paper)(({ theme }) => ({
  borderRadius: `calc(${theme.shape.borderRadius}px * 1.5)`,
  minWidth: 180,
  boxShadow: theme.shadows[8],
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
}));

export interface MenuItemData {
  /**
   * Unique identifier for the menu item
   */
  id: string;
  /**
   * Display label
   */
  label?: string;
  /**
   * Optional icon
   */
  icon?: React.ReactNode;
  /**
   * Optional secondary text
   */
  secondary?: string;
  /**
   * Whether the item is disabled
   */
  disabled?: boolean;
  /**
   * Whether this is a divider
   */
  divider?: boolean;
  /**
   * Click handler
   */
  onClick?: () => void;
  /**
   * Color variant for the item
   */
  color?: 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  /**
   * Submenu items
   */
  submenu?: MenuItemData[];
}

export interface MenuProps extends Omit<MuiMenuProps, 'children' | 'variant'> {
  /**
   * Menu items data
   */
  items: MenuItemData[];
  /**
   * Whether the menu is open
   */
  open: boolean;
  /**
   * Anchor element for positioning
   */
  anchorEl?: null | HTMLElement;
  /**
   * Callback fired when menu should close
   */
  onClose: () => void;
  /**
   * Menu variant
   */
  variant?: 'dropdown' | 'context';
  /**
   * Animation type
   */
  animation?: 'fade' | 'grow';
  /**
   * Whether to show icons column even when some items don't have icons
   */
  showIconsColumn?: boolean;
  /**
   * Maximum height before scrolling
   */
  maxHeight?: number;
}

export interface ContextMenuProps {
  /**
   * Menu items data
   */
  items: MenuItemData[];
  /**
   * Children that trigger the context menu
   */
  children: React.ReactNode;
  /**
   * Whether context menu is disabled
   */
  disabled?: boolean;
  /**
   * Animation type
   */
  animation?: 'fade' | 'grow';
}

export interface MenuButtonProps {
  /**
   * Menu items data
   */
  items: MenuItemData[];
  /**
   * Trigger button content (defaults to MoreVert icon)
   */
  children?: React.ReactNode;
  /**
   * Button variant
   */
  variant?: 'icon' | 'text' | 'contained' | 'outlined';
  /**
   * Button size
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * Whether button is disabled
   */
  disabled?: boolean;
  /**
   * Custom button props
   */
  buttonProps?: Partial<IconButtonProps>;
}

const renderMenuItem = (
  item: MenuItemData,
  index: number,
  onClose: () => void,
  showIconsColumn: boolean = false
) => {
  if (item.divider) {
    return <Divider key={`divider-${index}`} sx={{ my: 0.5 }} />;
  }

  const handleClick = () => {
    item.onClick?.();
    onClose();
  };

  return (
    <MenuItem
      key={item.id}
      onClick={handleClick}
      disabled={item.disabled}
      sx={{
        ...(item.color && item.color !== 'default' && {
          color: `${item.color}.main`,
          '& .MuiListItemIcon-root': {
            color: `${item.color}.main`,
          },
        }),
      }}
    >
      {(item.icon || showIconsColumn) && (
        <ListItemIcon>
          {item.icon}
        </ListItemIcon>
      )}
      <ListItemText
        primary={item.label}
        secondary={item.secondary}
      />
    </MenuItem>
  );
};

export const Menu: React.FC<MenuProps> = ({
  items,
  open,
  anchorEl,
  onClose,
  variant = 'dropdown',
  animation = 'fade',
  showIconsColumn = false,
  maxHeight = 300,
  ...props
}) => {
  const TransitionComponent = animation === 'grow' ? Grow : Fade;

  if (variant === 'context') {
    return (
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        transition
        disablePortal
      >
        {({ TransitionProps }) => (
          <TransitionComponent {...TransitionProps}>
            <ContextMenuContainer>
              <ClickAwayListener onClickAway={onClose}>
                <MenuList
                  autoFocusItem={open}
                  sx={{ py: 1, maxHeight, overflowY: 'auto' }}
                >
                  {items.map((item, index) =>
                    renderMenuItem(item, index, onClose, showIconsColumn)
                  )}
                </MenuList>
              </ClickAwayListener>
            </ContextMenuContainer>
          </TransitionComponent>
        )}
      </Popper>
    );
  }

  return (
    <StyledMenu
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      TransitionComponent={TransitionComponent}
      {...props}
      PaperProps={{
        ...props.PaperProps,
        sx: {
          maxHeight,
          overflowY: 'auto',
          ...props.PaperProps?.sx,
        },
      }}
    >
      {items.map((item, index) =>
        renderMenuItem(item, index, onClose, showIconsColumn)
      )}
    </StyledMenu>
  );
};

export const ContextMenu: React.FC<ContextMenuProps> = ({
  items,
  children,
  disabled = false,
  animation = 'fade',
}) => {
  const [contextMenu, setContextMenu] = React.useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);

  const handleContextMenu = (event: React.MouseEvent) => {
    if (disabled) return;
    
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : null
    );
  };

  const handleClose = () => {
    setContextMenu(null);
  };

  return (
    <>
      <div onContextMenu={handleContextMenu} style={{ cursor: disabled ? 'default' : 'context-menu' }}>
        {children}
      </div>
      <Menu
        open={contextMenu !== null}
        onClose={handleClose}
        variant="context"
        animation={animation}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        items={items}
      />
    </>
  );
};

export const MenuButton: React.FC<MenuButtonProps> = ({
  items,
  children,
  variant = 'icon',
  size = 'medium',
  disabled = false,
  buttonProps = {},
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  if (variant === 'icon') {
    return (
      <>
        <IconButton
          onClick={handleClick}
          size={size}
          disabled={disabled}
          {...buttonProps}
        >
          {children || <MoreVert />}
        </IconButton>
        <Menu
          items={items}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
        />
      </>
    );
  }

  // For other variants, you could implement Button component here
  return null;
}; 